import { useState } from 'react';
import { plural, t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import _ from 'lodash';
import moment from 'moment';
import Button from '@material-ui/core/Button';

import {
    ADMIN_USER_CANNOT_DELETE_OTHER_ADMIN_KEY,
    CANNOT_DELETE_DEFAULT_KEY,
    CANNOT_DELETE_SELF_KEY,
    EMAIL_FIELD,
    FIRST_NAME_FIELD,
    LAST_NAME_FIELD,
    LOCKED_OUT_UNTIL_FIELD,
    ROLE_NAMES,
    SOURCE,
    USER_DETAIL_NOT_ALLOWED_KEY,
    USER_NAME_FIELD,
    USER_ROLE_FIELD,
    USER_STATUS_FIELD,
    USER_STATUS_NAMES,
} from './UserConstants';
import { SELECTIONMODE_MULTI } from '../../data-table/SelectionContext';
import DataTable from '../../data-table/DataTable';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryLoader } from '../../data-table/Loaders';
import { getSystemTime } from '../../../stores/SystemTimeStore';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import { useFormDeleteService, useFormDeleteWrapper } from '../../../services/FormDeleteService';
import { noTranslated, yesTranslated } from '../../form/FormConstants';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import { GENERIC_SERVER_ERROR } from '../../../services/FormErrorService';
import useUiAlerts, { createAlertConfig, ERROR, INFO } from '../../../stores/AlertsStore';
import UserInvite from './UserInvite';
import { SORT_ASC, useSuccessfulAction } from '../../../utilities/FormUtilities';
import Link from '../../links/Link';
import { AdminPages, buildPageTarget, buildPageId, buildCreatePageTarget } from '../AdminLinkUtils';
import { useIntervalRefreshTrigger } from '../../data-table/Triggers';

const functionName = AdminPages.Users;

function getColumns() {
    return [
        {
            type: COLUMN_TYPES.LINK,
            mapping: USER_NAME_FIELD,
            title: t`User Name`,
            sortable: true,
            width: 300,
            link: {
                target: buildPageTarget(functionName),
            },
        },
        {
            type: COLUMN_TYPES.LINK,
            mapping: EMAIL_FIELD,
            title: t`Email`,
            sortable: true,
            width: 300,
            link: {
                target: buildPageTarget(functionName),
            },
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapper: (row) => _.trim(`${_.get(row, FIRST_NAME_FIELD, '')} ${_.get(row, LAST_NAME_FIELD, '')}`),
            title: t`Full Name`,
            sortProperty: FIRST_NAME_FIELD,
            sortable: true,
            width: 300,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: USER_ROLE_FIELD,
            title: t`Role`,
            sortable: true,
            formatter: (role) => ROLE_NAMES[role],
            width: 130,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: SOURCE,
            title: t`Source`,
            sortable: true,
            width: 100,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: USER_STATUS_FIELD,
            title: t`Status`,
            sortable: true,
            formatter: (status) => USER_STATUS_NAMES[status],
            width: 110,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: LOCKED_OUT_UNTIL_FIELD,
            title: t`Locked Out`,
            sortable: true,
            formatter: (lockedOutUntil) =>
                lockedOutUntil && moment(lockedOutUntil).isAfter(getSystemTime()) ? yesTranslated : noTranslated,
            width: 125,
        },
    ];
}

const initialSort = [{ property: USER_NAME_FIELD, direction: SORT_ASC }];

const dataSource = {
    url: '/ipm/v1/admin/users',
    totalProperty: 'count',
    dataProperty: 'data',
    params: {
        searchFields: [
            EMAIL_FIELD,
            USER_NAME_FIELD,
            FIRST_NAME_FIELD,
            LAST_NAME_FIELD,
            USER_ROLE_FIELD,
            SOURCE,
            USER_STATUS_FIELD,
        ],
    },
};

function collectDeleteErrors(deleteErrors) {
    let permissionDenied = false;
    let deleteSelf = false;
    let genericError = false;
    const errors = [];

    errors.push(
        i18n._(
            plural(deleteErrors.length, {
                one: `${deleteErrors.length} user was not deleted because of the following error:`,
                other: `${deleteErrors.length} users were not deleted because of the following error(s):`,
            })
        )
    );

    // Loop through and build out our errors.
    _.each(deleteErrors, (error) => {
        if (error === USER_DETAIL_NOT_ALLOWED_KEY) {
            if (permissionDenied === false) {
                errors.push(i18n._(t`You do not have permission to modify this user.`));
                permissionDenied = true;
            }
        } else if (error === CANNOT_DELETE_SELF_KEY) {
            if (deleteSelf === false) {
                errors.push(i18n._(t`You can't delete yourself.`));
                deleteSelf = true;
            }
        } else if (error === CANNOT_DELETE_DEFAULT_KEY) {
            errors.push(i18n._(t`Default user administrator cannot be removed from the system.`));
        } else if (error === ADMIN_USER_CANNOT_DELETE_OTHER_ADMIN_KEY) {
            errors.push(i18n._(t`'An administrator is not allowed to delete another administrator.`));
        } else if (genericError === false) {
            genericError = true;
            errors.push(i18n._(GENERIC_SERVER_ERROR));
        }
    });

    return errors;
}

export default function UserAdminList() {
    const formDeleteService = useFormDeleteService();
    const { deleteWrapper } = useFormDeleteWrapper();
    const [actionsDisabled, setActionsDisabled] = useState(false);
    const [tableState, setTableState] = useState(undefined);
    const [tableSelection, setTableSelection] = useState(undefined);
    const handleSuccessfulDelete = useSuccessfulAction();
    const [, uiAlertActions] = useUiAlerts();
    const deleteUrl = `${dataSource.url}/selection/remove`;
    const { triggerRef, trigger: refresher } = useIntervalRefreshTrigger();

    // Used by the data table to load data.
    const loader = useQueryLoader(dataSource, { disableFilterState: true });

    const postDeleteHandler = (response) => {
        const { results } = response;
        const deleteErrors = _.chain(results)
            .filter({ deleted: false })
            .map((r) => _.get(r, 'error.message'))
            .value();

        if (_.isEmpty(deleteErrors)) {
            uiAlertActions.createAlert(
                createAlertConfig(
                    INFO,
                    t`Please check Notifications and Scheduled Reports for obsolete e-mail recipients`
                )
            );
            return handleSuccessfulDelete(response, null, refresher);
        }

        uiAlertActions.createAlert(createAlertConfig(ERROR, collectDeleteErrors(deleteErrors).join('  ')));
    };

    const handleConfirmedDelete = () => {
        const params = _.merge(
            {
                search: tableState?.search || '',
            },
            dataSource.params
        );

        return deleteWrapper({
            deleteFunction: () => formDeleteService.deleteSelection(deleteUrl, params, tableSelection),
            refresherFunc: refresher,
            postDeleteHandler,
        })();
    };

    const handleTableSelectionChange = (currentTableSelection) => {
        setTableSelection(currentTableSelection);
        setActionsDisabled(
            !currentTableSelection || (_.isEmpty(currentTableSelection.selection) && !currentTableSelection.inverted)
        );
    };

    return (
        <AdminPage
            title={t`Users`}
            pageId={buildPageId(functionName)}
            actions={
                <Actions>
                    <ConfirmDeleteButton
                        disabled={actionsDisabled}
                        handleConfirmedDelete={handleConfirmedDelete}
                        plural={tableSelection?.selection?.length > 1}
                    />
                    <Button
                        component={Link}
                        to={buildCreatePageTarget(functionName)}
                        variant="contained"
                        color="primary"
                    >
                        <Trans>Create</Trans>
                    </Button>
                    <UserInvite refresherFunc={refresher} />
                </Actions>
            }
        >
            <DataTable
                loader={loader}
                triggerRef={triggerRef}
                columns={getColumns()}
                tableId={functionName}
                visibleRows={0}
                showSelectionColumn
                sorters={initialSort}
                onStateChanged={setTableState}
                selectionOptions={{
                    mode: SELECTIONMODE_MULTI,
                    allowSelectAll: true,
                    idMapping: '_id',
                    onChange: handleTableSelectionChange,
                }}
            />
        </AdminPage>
    );
}
